import { useState } from 'react';
import { Send, SlidersHorizontal, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { useNaturalLanguageQuery } from '../../hooks/useNaturalLanguageQuery';

interface NaturalLanguageInputProps {
  onSwitchMode: () => void;
}

const PLACEHOLDER_EXAMPLES = [
  'How many patients have consented to APCM?',
  'Diabetic patients we have not reached in 30 days',
  'Consented patients by primary care provider',
  'Completed calls per week over the last 2 months',
];

export function NaturalLanguageInput({ onSwitchMode }: NaturalLanguageInputProps) {
  const [placeholder] = useState(
    PLACEHOLDER_EXAMPLES[Math.floor(Math.random() * PLACEHOLDER_EXAMPLES.length)]
  );

  const { naturalLanguageInput, setNaturalLanguageInput, uiState } = useAnalyticsStore();
  const translateQuery = useNaturalLanguageQuery();

  const isTranslating = uiState === 'translating';

  const handleSubmit = () => {
    if (!naturalLanguageInput.trim() || isTranslating) return;
    translateQuery.mutate(naturalLanguageInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold">What would you like to know?</h3>
        <Button variant="ghost" size="sm" onClick={onSwitchMode}>
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Use filters instead
        </Button>
      </div>

      <div className="relative">
        <Textarea
          value={naturalLanguageInput}
          onChange={(e) => setNaturalLanguageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isTranslating}
          className="min-h-[100px] pr-12 resize-none"
        />
        <Button
          size="icon"
          onClick={handleSubmit}
          disabled={!naturalLanguageInput.trim() || isTranslating}
          className="absolute bottom-3 right-3"
        >
          {isTranslating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Press Enter to submit, Shift+Enter for new line
      </p>
    </div>
  );
}
